// Settings — users + organization API.
//
// Wire types derive from the backend OpenAPI codegen
// (`components["schemas"]["UserResponse"]` etc.). The narrowed
// `UserResponse` view-model retains non-optional invariants the UI relies
// on; the wire form is still available as `UserResponseWire` for
// MSW/handler-side consumption.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiRequest } from "@/lib/api-client";
import type { components } from "@/types/api";
import type { PageResponse } from "@/types/property";

// ---------------------------------------------------------------------------
// Wire types (1:1 with `components.schemas`)
// ---------------------------------------------------------------------------

export type UserResponseWire = components["schemas"]["UserResponse"];
export type UserRole = NonNullable<UserResponseWire["role"]>;
export type InviteUserRequest = components["schemas"]["UserInvitationRequest"];
export type UpdateUserRequest = components["schemas"]["UserUpdateRequest"];
export type OrganizationResponseWire = components["schemas"]["OrganizationResponse"];
export type UpdateOrganizationRequest =
  components["schemas"]["OrganizationUpdateRequest"];
export type BrokerSummaryWire = components["schemas"]["BrokerSummaryResponse"];

// ---------------------------------------------------------------------------
// View-model: tighten always-present scalars to non-optional. Consumers
// (team list, organization tab) read these fields unconditionally.
// ---------------------------------------------------------------------------

export interface UserResponse {
  id: number;
  email: string;
  fullName: string;
  phoneNumber: string | null;
  role: UserRole;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Slim shape returned by /users/brokers — used by AssignedBroker dropdowns. */
export interface BrokerSlim {
  id: number;
  fullName: string;
  role: UserRole;
}

/**
 * Counts of the "book" a user owns — the delete dialog previews these (what a
 * reassignment will move) and the delete response echoes back what actually moved.
 */
export interface UserOwnedSummary {
  properties: number;
  leads: number;
  customers: number;
  upcomingAppointments: number;
}

export interface OrganizationResponse {
  id: number;
  name: string;
  slug: string;
  phoneNumber: string | null;
  addressLine: string | null;
  plan: string;
  aiTokenQuotaMonthly: number;
  watermarkText: string;
  freshnessFreshDays: number;
  freshnessStaleDays: number;
}

// Frontend-only: URL search-param shape. Overlaps with the backend's
// `UserListQuery` codegen schema but kept locally so the optionality
// reflects client-state, not the wire DTO.
export interface UserListQuery {
  page?: number;
  size?: number;
  search?: string;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const userKeys = {
  all: ["users"] as const,
  lists: () => [...userKeys.all, "list"] as const,
  list: (query: UserListQuery) => [...userKeys.lists(), query] as const,
  brokers: () => [...userKeys.all, "brokers"] as const,
  detail: (id: number) => [...userKeys.all, "detail", id] as const,
};

export const organizationKeys = {
  all: ["organization"] as const,
  current: () => [...organizationKeys.all, "current"] as const,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildUserListPath(query: UserListQuery): string {
  const params = new URLSearchParams();
  if (query.page !== undefined) params.set("page", String(query.page));
  if (query.size !== undefined) params.set("size", String(query.size));
  if (query.search !== undefined && query.search !== "")
    params.set("search", query.search);
  const qs = params.toString();
  return qs ? `/users?${qs}` : "/users";
}

function syncUserIntoCache(
  queryClient: QueryClient,
  user: UserResponse,
): void {
  queryClient.setQueryData(userKeys.detail(user.id), user);
  void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: userKeys.brokers() });
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export function useTeamUsers(query: UserListQuery, enabled = true) {
  return useQuery({
    queryKey: userKeys.list(query),
    queryFn: () => apiRequest<PageResponse<UserResponse>>(buildUserListPath(query)),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useBrokers() {
  return useQuery({
    queryKey: userKeys.brokers(),
    queryFn: () => apiRequest<BrokerSlim[]>("/users/brokers"),
    staleTime: 60_000,
  });
}

/** Preview of what a user owns, for the delete-and-reassign dialog. */
export function useUserOwnedSummary(id: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...userKeys.all, "owned-summary", id ?? "none"] as const,
    queryFn: () => apiRequest<UserOwnedSummary>(`/users/${id}/owned-summary`),
    enabled: enabled && id !== undefined,
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteUserRequest) =>
      apiRequest<UserResponse>("/users/invitations", {
        method: "POST",
        body,
      }),
    onSuccess: (user) => {
      syncUserIntoCache(queryClient, user);
    },
  });
}

interface UpdateUserInput {
  id: number;
  patch: UpdateUserRequest;
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: UpdateUserInput) =>
      apiRequest<UserResponse>(`/users/${id}`, { method: "PATCH", body: patch }),
    // Optimistic update on the cached list so the row repaints
    // instantly. Rolls back on error.
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: userKeys.lists() });
      const snapshots = queryClient.getQueriesData<PageResponse<UserResponse>>({
        queryKey: userKeys.lists(),
      });
      for (const [key, data] of snapshots) {
        if (data === undefined) continue;
        queryClient.setQueryData<PageResponse<UserResponse>>(key, {
          ...data,
          items: data.items.map((u) =>
            u.id === id ? { ...u, ...patch, phoneNumber: patch.phoneNumber ?? u.phoneNumber } : u,
          ),
        });
      }
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.snapshots) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: (data) => {
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: userKeys.brokers() });
      if (data !== undefined) {
        queryClient.setQueryData(userKeys.detail(data.id), data);
      }
    },
  });
}

interface DeleteUserInput {
  id: number;
  /** Who inherits the deleted user's book (properties/leads/customers/appointments). */
  reassignTo: number;
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reassignTo }: DeleteUserInput) =>
      apiRequest<UserOwnedSummary>(`/users/${id}?reassignTo=${reassignTo}`, {
        method: "DELETE",
      }),
    onSuccess: (_data, { id }) => {
      queryClient.removeQueries({ queryKey: userKeys.detail(id) });
      // The book moved to another broker — refresh every list that shows ownership.
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: userKeys.brokers() });
      void queryClient.invalidateQueries({ queryKey: ["properties"] });
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
      void queryClient.invalidateQueries({ queryKey: ["appointments"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Organization
// ---------------------------------------------------------------------------

export function useOrganization() {
  return useQuery({
    queryKey: organizationKeys.current(),
    queryFn: () => apiRequest<OrganizationResponse>("/organization"),
    // The org rarely changes within a session; cache aggressively.
    staleTime: 5 * 60_000,
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateOrganizationRequest) =>
      apiRequest<OrganizationResponse>("/organization", {
        method: "PATCH",
        body,
      }),
    onSuccess: (org) => {
      queryClient.setQueryData(organizationKeys.current(), org);
    },
  });
}
